import { inject, signal, WritableSignal } from '@angular/core'
import { KeyString } from '@app/core/models/custom/misc.model'
import { ProductVariantCategoryElement } from '@app/core/models/custom/product/variant.model'
import { OrderEntry } from '@spartacus/cart/base/root'
import { Product, ProductService, VariantMatrixElement } from '@spartacus/core'
import { BehaviorSubject, catchError, filter, map, Observable, of, take, tap } from 'rxjs'
import { environment } from 'src/environments/environment'

export class CustomProductService {
  private readonly productService = inject(ProductService)

  private productIdClicked: string | undefined
  private productPagesLoaded: number | undefined
  private categoryClicked: string | undefined
  private currentProduct$ = new BehaviorSubject<Product | undefined>(undefined)
  private isWaistApplicable$ = new BehaviorSubject<Record<string, boolean>>({})

  private _isFetchingCurrentProduct$ = new BehaviorSubject<boolean>(false)
  isFetchingCurrentProduct$ = this._isFetchingCurrentProduct$.asObservable()

  currentProductImage: WritableSignal<string | undefined> = signal('')
  colors = signal([] as ProductVariantCategoryElement[])

  getProductImageUrl (product: Product, format?: 'thumbnail' | 'swatchPicture' | 'productImage' | 'primaryImage' | 'none', categoryCode?: string): string {
    if (product) {
      const scene7BaseUrl = environment.scene7baseURL + 'CCG_'

      // Fallback to baseProduct code if manufacturerAID is empty
      const manufacturerAID = product.manufacturerAID ?? product.baseProduct ?? product.code
      let variantCode: string | undefined

      const variantMatrixElements = product.variantMatrix || []
      if (variantMatrixElements.length > 0 && product.baseProduct) {
        for (const variant of variantMatrixElements) {
          if (variant?.parentVariantCategory?.name?.toLowerCase().includes('color')) {
            variantCode = variant.variantValueCategory?.code
              ?.replace('COLOR_', '')
              ?.replace('_SW', '')
            break
          }
        }
      } else if (product.colorCode) {
        variantCode = product.colorCode
      }

      const defaultImage = product.availableDefaultImagesCode
      if (defaultImage && defaultImage.length > 0 && categoryCode) {
        const imageName = defaultImage.find(img => img.categoryCode === categoryCode)?.imageName
        if (imageName) {
          return scene7BaseUrl + imageName + this.getSuffix(format)
        } else {
          return scene7BaseUrl + defaultImage[0] + this.getSuffix(format)
        }
      }

      if (variantCode) {
        return scene7BaseUrl + manufacturerAID + '_' + variantCode + this.getSuffix(format)
      }
    }

    return ''
  }

  getThumbnailForProduct (product: Product): string | undefined {
    return this.getProductImageUrl(product, 'thumbnail')
  }

  getImageForProduct (product: Product): string | undefined {
    return this.getProductImageUrl(product, 'productImage')
  }

  private getSuffix (format?: string): string {
    if (format) {
      switch (format) {
      case 'primaryImage':
        return '?' + environment.scene7primaryImageKey
      case 'productImage':
        return '?' + environment.scene7ProductImageKey
      case 'swatchPicture':
        return '?' + environment.scene7swatchPictureParam
      case 'none':
        return ''
      default:
        return '?' + environment.scene7thumbnailImageKey
      }
    }

    return '?' + environment.scene7thumbnailImageKey
  }

  get productIdFromPLP (): string | undefined {
    return this.productIdClicked
  }

  get productPagesLoadedOnPLP (): number | undefined {
    return this.productPagesLoaded
  }

  get productCategoryFromPLP (): string | undefined {
    return this.categoryClicked
  }

  setIsFetchingCurrentProduct (value: boolean): void {
    this._isFetchingCurrentProduct$.next(value)
  }

  setStoreElementFromPLP (elementToStore: HTMLElement | undefined, pageNumber: number | undefined) {
    this.productIdClicked = elementToStore?.id
    this.productPagesLoaded = pageNumber
  }

  setCurrentProduct (product: Product): void {
    this.currentProduct$.next(product)
    this.fetchAndSetCurrentProductImage(product!)
    this.setIsFetchingCurrentProduct(false)
  }

  initializeCurrentProduct (product: Product, sku: string, colorCode?: string): void {
    this.buildColorOptions(product)
    this.getFirstVariantProductIfNeeded(product, sku, colorCode).pipe(
      take(1),
      tap(finalProduct => {
        this.currentProduct$.next(finalProduct!)
        this.fetchAndSetCurrentProductImage(finalProduct!)
      })
    ).subscribe()
  }

  getCmsProductVariant (productCode: string): Observable<Product> {
    return this.productService.get(productCode, 'quickview').pipe(
      filter(value => Boolean(value)),
      map(product => product as Product)
    )
  }

  private getFirstVariantProductIfNeeded (product: Product, sku: string, colorCode?: string): Observable<Product | null> {
    const activeColor = this.getActiveColor(product, sku, colorCode)

    if (!activeColor) {
      const variantSku = this.getFirstColorSku()

      if (variantSku) {
        return this.fetchProduct(variantSku)
      } else {
        return of(product)
      }
    }

    const displayProduct: Product = {
      ...product,
      code: sku,
      baseProduct: product.code,
      colorCode: activeColor.variantValueCategory?.code?.split('_')?.[0],
      colorLabel: activeColor.variantValueCategory?.label,
      price: activeColor.variantOption?.priceData,
      stock: activeColor.variantOption?.stock
    }

    return of(displayProduct)
  }

  private fetchProduct (sku: string): Observable<Product | null> {
    if (!sku) {
      return of(null)
    }
    return this.productService.get(sku, ['variants']).pipe(
      filter((product): product is Product => !!product),
      take(1),
      catchError(() => of(null))
    )
  }

  private getActiveColor (product: Product, sku: string, colorCode?: string): VariantMatrixElement | undefined {
    const colors: VariantMatrixElement[] = product.variantMatrix || []

    if (colorCode) {
      const foundColor = colors.find(color => {
        const matrixColorCode = color.variantValueCategory?.code?.split('_')?.[0]
        return matrixColorCode === colorCode
      })
      if (foundColor) {
        return foundColor
      }
    }

    const foundColor = colors.find(color => {
      return color.variantOption?.code === sku
    })

    return foundColor
  }

  private getFirstColorSku (): string {
    const firstColor: ProductVariantCategoryElement = this.colors()?.[0] ?? null
    const variantCode = firstColor?.variantCode ?? ''
    return variantCode
  }

  private buildColorOptions (product: Product): void {
    const allColorElements = this.splitElementsByColor(product!)

    const matrix: ProductVariantCategoryElement[] = this.convertVariantMatrixData(product.variantMatrix!)

    const sorted = this.sortVariantsBySequence(matrix)?.map(color => ({
      ...color,
      available: !!allColorElements[color.code]?.some(item => !!item.variantOption?.stock?.stockLevel)
    }))

    this.colors.set(sorted)
  }

  private sortVariantsBySequence<T extends { sequence: number }> (matrix: T[]): T[] {
    return matrix?.sort((a, b) => {
      const aSequence: number = a.sequence || 0
      const bSequence: number = b.sequence || 0

      return aSequence - bSequence
    })
  }

  private splitElementsByColor (product: Product): KeyString<VariantMatrixElement[]> {
    const variantMatrix = product.variantMatrix ?? []

    const colorElements: KeyString<VariantMatrixElement[]> = {}

    for (const color of variantMatrix) {
      const colorCode = color.variantValueCategory?.code ?? ''

      colorElements[colorCode] = this.extractColorLeafs(color.elements!)
    }

    return colorElements
  }

  private convertVariantMatrixData (
    data: VariantMatrixElement[],
    customValues: Partial<ProductVariantCategoryElement> = {}
  ): ProductVariantCategoryElement[] {
    return data?.map(element => ({
      code: element.variantValueCategory?.code || '',
      label: element.variantValueCategory?.label || '',
      name: element.variantValueCategory?.name || '',
      variantCode: element.variantOption?.code || '',
      category: customValues.category ?? '',
      price: element.variantOption?.priceData,
      stock: element.variantOption?.stock,
      sequence: element.variantValueCategory?.sequence || 0,
      ...customValues
    }))
  }

  private extractColorLeafs (matrix: VariantMatrixElement[]): VariantMatrixElement[] {
    return matrix.flatMap(item => item.elements?.length
      ? [item, ...this.extractColorLeafs(item?.elements ?? [])]
      : item
    ).filter(item => item.isLeaf)
  }

  getCurrentProduct$ (): Observable<Product | undefined> {
    return this.currentProduct$.asObservable()
  }

  buildProductPrettyName (productName: string): string {
    if (!productName) return ''

    const firstStep = productName.replace(/ /g, '-')
    const secondStep = firstStep.replace(/[^a-zA-Z0-9-]/g, '')
    return secondStep.toLowerCase() || ''
  }

  placeholderImage (): string {
    return environment.scene7baseURL + 'CCG_'
  }

  getProductBadges (product: Product): string[] {
    // get up to 2 badges
    return product.badges
      ?.filter(b => !!b?.badgeType?.imageUrl)
      ?.slice(0, 2)
      .map(b => b.badgeType!.imageUrl) ?? []
  }

  getProductVariant (
    item: OrderEntry | undefined,
    value: string,
    avoidExtraTransformation: boolean = false,
    pageName?: string
  ) {
    let result: string = ''
    let waist: string = ''
    let length: string = ''
    let shoeSize: string = ''
    let shoeWidth: string = ''
    let extendedSize: string = ''
    const sizeCategory = this.getProductVariantCategory(item)
    if (pageName === 'cart' && (sizeCategory === 'REG' || sizeCategory === 'TLL')) {
      this.isWaistApplicable(item)
    }
    const hasWaist = !!this.isWaistApplicable$.value[item?.product?.baseProduct as any]
    item?.product?.baseOptions?.map((option) => {
      option.selected?.variantOptionQualifiers?.map((qualifier) => {
        if (qualifier.qualifier?.includes('ALPHA_SIZE') && value !== 'Color') {
          result = qualifier?.value as string
        }
        if (qualifier.qualifier?.includes('WAIST') && value !== 'Color') {
          waist = qualifier.value as string
        }
        if (qualifier.qualifier?.includes('NUMERIC_SIZE') && !qualifier?.qualifier?.includes('FOOTWEAR_NUMERIC') && value !== 'Color') {
          waist = qualifier.value as string
        }
        if (qualifier.qualifier?.includes('LENGTH') && value !== 'Color') {
          length = qualifier.value as string
        }
        if (qualifier.qualifier?.includes('ALPHA_SIZE') || qualifier.qualifier?.includes('ALPHA_COMBO')) {
          result = qualifier?.value as string
        }
        if (qualifier.qualifier?.includes('ALPHA_LENGTH_2') && !item?.product?.numericProduct && qualifier.value !== 'REG' && !hasWaist) {
          extendedSize = qualifier.value as string
        }
        if (qualifier?.name === value || qualifier?.name?.includes(value)) {
          result = qualifier?.value as string
        }
        if (qualifier?.qualifier?.includes('FOOTWEAR_NUMERIC')) {
          shoeSize = qualifier?.value as string
        }
        if (qualifier.qualifier?.includes('FOOTWEAR_WIDTH')) {
          shoeWidth = qualifier?.value as string
        }
      })
    })

    if (!avoidExtraTransformation) {
      if (waist && length) {
        result = `${waist} x ${length}`
      }

      if (waist && !length) {
        result = waist
      }

      if (!waist && hasWaist) {
        result = `${result} x ${length}`
      }

      if (shoeSize && shoeWidth) {
        result = `${shoeSize} ${shoeWidth}`
      }

      if (extendedSize) {
        const formattedSize = extendedSize.toLowerCase()
        const capitalizedSize = formattedSize.charAt(0).toUpperCase() + formattedSize.slice(1)
        result = `${result} (${capitalizedSize})`
      }
    }

    return result
  }

  private fetchAndSetCurrentProductImage (product: Product): void {
    const image = this.getImageForProduct(product)
    this.currentProductImage.set(image)
  }

  private getProductVariantCategory (item: OrderEntry | undefined) {
    let result: string = ''

    item?.product?.baseOptions?.map((option) => {
      option.selected?.variantOptionQualifiers?.map((qualifier) => {
        if (qualifier.qualifier?.includes('LOS_L_ONE_SIZE')) {
          result = qualifier?.value as string
        }
        if (qualifier.qualifier?.includes('MAA1_M_ALPHA_LENGTH_2')) {
          result = qualifier?.value as string
        }
      })
    })

    return result
  }

  private isWaistApplicable(item: OrderEntry | any) {
    const productCode = item?.product?.baseProduct

    const cached = this.getWaistState()[productCode]
    if (cached !== undefined) return

    this.getCmsProductVariant(productCode).pipe(take(1)).subscribe(finalProduct => {
      const activeColor = this.getActiveColorOption(finalProduct, item?.product?.colorCode)

      const hasWaist = (activeColor?.elements || []).some((element: VariantMatrixElement) => {
        const categoryLabel = element.variantValueCategory?.label || ''
        const categoryCode = element.variantValueCategory?.code || ''

        const isTall = /tall|tll/i.test(categoryLabel)
        const isRegular = /regular|reg/i.test(categoryLabel)
        const isLength = /length/i.test(categoryLabel) || /length/i.test(categoryCode)

        return (
          isLength &&
          !isRegular &&
          (!isTall || (isTall && finalProduct?.numericProduct))
        )
      })

      this.isWaistApplicable$.next({
        ...this.getWaistState(),
        [productCode]: hasWaist
      })
    })
  }

  private getWaistState() {
    return this.isWaistApplicable$.value
  }

  private getActiveColorOption (product: Product, colorCode: String): VariantMatrixElement | undefined {
    const colors: VariantMatrixElement[] = product.variantMatrix || []
    const updatedProductCode = `${product.code}-${colorCode}`
    return colors.find(color => color.variantOption?.code?.includes(updatedProductCode as string))
  }
}
